import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from "../../../../store/eyeconxStore";
import usersService from "../../../../services/users.service";
import Loading from "../../../common/Loading";

const UserReminder = ({ show, user, onClose }) => {
    const clinicId = useEyeconxStore(state => state.user.clinicId);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [settings, setSettings] = useState(null);

    const { register, handleSubmit, watch, reset, setValue, formState: { errors, isDirty } } = useForm({
        defaultValues: settings
    });

    const formValues = watch();

    useEffect(() => {
        if (show && user) {
            fetchUserReminderSettings();
        }
    }, [show, user]);

    const fetchUserReminderSettings = async () => {
        try {
            setIsLoading(true);
            const response = await usersService.getUserReminder(clinicId, user.id);
            //console.log(response);
            setSettings(response.data);
            reset(response.data);
        } catch (error) {
            console.error('Error fetching user reminder settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
        reset(settings);
    };

    const handleCancel = () => {
        setIsEditing(false);
        reset(settings);
    };

    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            await usersService.updateUserReminder(clinicId, user.id, data);
            const updatedSettings = await usersService.getUserReminder(clinicId, user.id);
            setSettings(updatedSettings.data);
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating user reminder settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Custom Time Input Component
    const TimeInput = ({ name, label, description, error }) => {
        const currentValue = isEditing ? formValues[name] : settings?.[name];

        const parseTime = (timeString) => {
            if (!timeString) return { hours: "12", minutes: "00", period: "AM" };

            // Match 24-hour format: "22:00:00"
            const match24 = timeString.match(/^(\d{1,2}):(\d{2})/);
            if (match24) {
                let hours = parseInt(match24[1], 10);
                const minutes = match24[2];
                const period = hours >= 12 ? "PM" : "AM";
                hours = hours % 12 || 12;
                return {
                    hours: hours.toString().padStart(2, "0"),
                    minutes,
                    period,
                };
            }

            // Match 12-hour format fallback: "10:30 AM"
            const match12 = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
            if (match12) {
                return {
                    hours: match12[1],
                    minutes: match12[2],
                    period: match12[3].toUpperCase(),
                };
            }

            return { hours: "12", minutes: "00", period: "AM" };
        };

        const [time, setTime] = useState(parseTime(currentValue));

        const convertTo24Hour = ({ hours, minutes, period }) => {
            let hrs = parseInt(hours, 10);
            if (period === "PM" && hrs < 12) hrs += 12;
            if (period === "AM" && hrs === 12) hrs = 0;
            return `${hrs.toString().padStart(2, "0")}:${minutes}:00`;
        };

        const handleTimeChange = (field, value) => {
            const newTime = { ...time, [field]: value };
            setTime(newTime);

            const formatted24 = convertTo24Hour(newTime);
            setValue(name, formatted24, { shouldDirty: true });
        };

        const hoursOptions = Array.from({ length: 12 }, (_, i) =>
            (i + 1).toString().padStart(2, "0")
        );
        const minutesOptions = Array.from({ length: 60 }, (_, i) =>
            i.toString().padStart(2, "0")
        );

        const displayValue = time
            ? `${parseInt(time.hours)}:${time.minutes} ${time.period}`
            : "—";

        return (
            <div className="setting-item">
                <label className="form-label fw-semibold mb-2">{label}</label>
                {description && <p className="text-muted small mb-3">{description}</p>}

                {isEditing ? (
                    <div>
                        <div className="d-flex gap-2 align-items-center">
                            <select
                                className={`form-select ${error ? "is-invalid" : ""}`}
                                value={time.hours}
                                onChange={(e) => handleTimeChange("hours", e.target.value)}
                            >
                                {hoursOptions.map((hour) => (
                                    <option key={hour} value={hour}>
                                        {hour}
                                    </option>
                                ))}
                            </select>

                            <span className="text-muted">:</span>

                            <select
                                className={`form-select ${error ? "is-invalid" : ""}`}
                                value={time.minutes}
                                onChange={(e) => handleTimeChange("minutes", e.target.value)}
                            >
                                {minutesOptions.map((minute) => (
                                    <option key={minute} value={minute}>
                                        {minute}
                                    </option>
                                ))}
                            </select>

                            <select
                                className={`form-select ${error ? "is-invalid" : ""}`}
                                value={time.period}
                                onChange={(e) => handleTimeChange("period", e.target.value)}
                            >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                            </select>
                        </div>

                        {error && <div className="invalid-feedback d-block">{error.message}</div>}
                        <input type="hidden" {...register(name)} />
                    </div>
                ) : (
                    <div className="display-value">
                        <span className="text-dark fw-medium">{displayValue}</span>
                    </div>
                )}
            </div>
        );
    };

    const SettingToggle = ({ name, label, description, register }) => {
        const currentValue = isEditing ? formValues[name] : settings?.[name];

        return (
            <div className="setting-item">
                <div className="d-flex justify-content-between align-items-start h-100">
                    <div className="flex-grow-1">
                        <label className="form-check-label fw-semibold mb-2" htmlFor={name}>
                            {label}
                        </label>
                        <p className="text-muted small mb-0">{description}</p>
                    </div>
                    <div className="ms-3 d-flex align-items-center">
                        {isEditing ? (
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={name}
                                    {...register(name)}
                                />
                            </div>
                        ) : (
                            <span className={`badge ${currentValue ? 'bg-success' : 'bg-secondary'}`}>
                                <i className={`bi ${currentValue ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                                {currentValue ? 'Enabled' : 'Disabled'}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const SettingInput = ({ name, label, type = "text", description, register, validation, error }) => {
        const currentValue = isEditing ? formValues[name] : settings?.[name];

        return (
            <div className="setting-item">
                <label htmlFor={name} className="form-label fw-semibold mb-2">
                    {label}
                </label>
                <p className="text-muted small mb-3">{description}</p>
                {isEditing ? (
                    <input
                        type={type}
                        className={`form-control ${error ? 'is-invalid' : ''}`}
                        id={name}
                        {...register(name, validation)}
                    />
                ) : (
                    <div className="display-value">
                        <span className="text-dark fw-medium">{currentValue}</span>
                    </div>
                )}
                {error && <div className="invalid-feedback">{error.message}</div>}
            </div>
        );
    };

    const SettingSelect = ({ name, label, options, description, register, error }) => {
        const currentValue = isEditing ? formValues[name] : settings?.[name];

        const getOptionLabel = (value) => {
            const option = options.find(opt => opt.value === value);
            return option ? option.label : value;
        };

        return (
            <div className="setting-item">
                <label htmlFor={name} className="form-label fw-semibold mb-2">
                    {label}
                </label>
                <p className="text-muted small mb-3">{description}</p>
                {isEditing ? (
                    <select
                        className={`form-select ${error ? 'is-invalid' : ''}`}
                        id={name}
                        {...register(name)}
                    >
                        {options.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                ) : (
                    <div className="display-value">
                        <span className="text-dark fw-medium">{getOptionLabel(currentValue)}</span>
                    </div>
                )}
                {error && <div className="invalid-feedback">{error.message}</div>}
            </div>
        );
    };

    if (!show) return null;

    return (
        <div
            className={`modal fade ${show ? 'show d-block' : ''}`}
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
        >
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                <div className="modal-content rounded-3 shadow-lg border-0">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title fw-bold text-white">
                            <i className="bi bi-journal-text me-2"></i>
                            Reminder Settings for {user?.firstName} {user?.lastName}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-lg btn-close-white"
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body p-4">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <Loading />
                                <p className="text-muted mt-2">Loading reminder settings...</p>
                            </div>
                        ) : settings ? (
                            <>
                                {/* Header Section */}
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <p className="text-muted mb-0">
                                            Manage reminder settings for {user?.firstName} {user?.lastName}
                                        </p>
                                    </div>
                                    {!isEditing ? (
                                        <button
                                            className="btn btn-outline-info text-dark px-4"
                                            onClick={handleEdit}
                                        >
                                            <i className="bi bi-pencil me-2"></i>
                                            Edit Settings
                                        </button>
                                    ) : (
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-secondary px-3"
                                                onClick={handleCancel}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-primary text-white px-4"
                                                onClick={handleSubmit(onSubmit)}
                                                disabled={!isDirty || isLoading}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-check-circle me-1"></i>
                                                        Save Changes
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Settings Form */}
                                <div className="row">
                                    <div className="col-12">
                                        <form onSubmit={handleSubmit(onSubmit)}>
                                            <div className="row g-4">
                                                {/* Basic Settings */}
                                                <div className="col-12">
                                                    <div className="card bg-light shadow-sm border-0 h-100">
                                                        <div className="card-header bg-transparent border-0 pb-0">
                                                            <h5 className="card-title mb-0">
                                                                <i className="bi bi-gear-fill text-primary me-2"></i>
                                                                Basic Settings
                                                            </h5>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row g-3">
                                                                <div className="col-6">
                                                                    <SettingToggle
                                                                        name="apptReminder"
                                                                        label="Appointment Reminders"
                                                                        description="Enable automated appointment reminders for patients"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <TimeInput
                                                                        name="reminderTime"
                                                                        label="Reminder Time"
                                                                        description="Default time to send daily reminders"
                                                                        error={errors.reminderTime}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingToggle
                                                                        name="isSameDayReminder"
                                                                        label="Same Day Reminders"
                                                                        description="Send reminders on the day of appointment"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <TimeInput
                                                                        name="sameDayReminderTime"
                                                                        label="Same Day Reminder Time"
                                                                        description="Time to send same-day appointment reminders"
                                                                        error={errors.sameDayReminderTime}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingToggle
                                                                        name="isPrebookedReminder"
                                                                        label="Prebooked Reminders"
                                                                        description="Enable automated prebooked reminders for patients"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingInput
                                                                        name="daysPrebookReminder"
                                                                        label="Prebook Reminder (Days)"
                                                                        type="number"
                                                                        description="Days for prebook reminder to send."
                                                                        register={register}
                                                                        validation={{ min: 0, max: 365 }}
                                                                        error={errors.daysFirstReminder}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Communication Channels */}
                                                <div className="col-xl-12">
                                                    <div className="card bg-light shadow-sm border-0 h-100">
                                                        <div className="card-header bg-transparent border-0 pb-0">
                                                            <h5 className="card-title mb-0">
                                                                <i className="bi bi-telephone-fill text-primary me-2"></i>
                                                                Communication Channels
                                                            </h5>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row g-3">
                                                                <div className="col-12">
                                                                    <SettingToggle
                                                                        name="isEmail"
                                                                        label="Email Notifications"
                                                                        description="Send reminders and notifications via email"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-12">
                                                                    <SettingToggle
                                                                        name="isSms"
                                                                        label="SMS Notifications"
                                                                        description="Send reminders via text message"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-12">
                                                                    <SettingToggle
                                                                        name="isCall"
                                                                        label="Phone Call Notifications"
                                                                        description="Send reminders via automated phone calls"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-12">
                                                                    <SettingSelect
                                                                        name="callPreference"
                                                                        label="Call Preference"
                                                                        description="Preferred phone number for automated calls"
                                                                        register={register}
                                                                        options={[
                                                                            { value: 'DISABLED', label: 'Disabled' },
                                                                            { value: 'HOME_PHONE', label: 'Home Phone' },
                                                                            { value: 'CELL_PHONE', label: 'Cell Phone' }
                                                                        ]}
                                                                        error={errors.callPreference}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Reminder Types */}
                                                <div className="col-xl-12">
                                                    <div className="card bg-light shadow-sm border-0 h-100">
                                                        <div className="card-header bg-transparent border-0 pb-0">
                                                            <h5 className="card-title mb-0">
                                                                <i className="bi bi-card-checklist text-primary me-2"></i>
                                                                Reminders
                                                            </h5>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row g-3">
                                                                <div className="col-6">
                                                                    <SettingToggle
                                                                        name="isFirstReminder"
                                                                        label="First Reminder"
                                                                        description="Send initial appointment reminder"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingInput
                                                                        name="daysFirstReminder"
                                                                        label="First Reminder (Days)"
                                                                        type="number"
                                                                        description="Days before appointment for first reminder"
                                                                        register={register}
                                                                        validation={{ min: 0, max: 365 }}
                                                                        error={errors.daysFirstReminder}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingToggle
                                                                        name="isSecondReminder"
                                                                        label="Second Reminder"
                                                                        description="Send follow-up reminder"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingInput
                                                                        name="daysSecondReminder"
                                                                        label="Second Reminder (Days)"
                                                                        type="number"
                                                                        description="Days before appointment for second reminder"
                                                                        register={register}
                                                                        validation={{ min: 0, max: 365 }}
                                                                        error={errors.daysSecondReminder}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingToggle
                                                                        name="isLastReminder"
                                                                        label="Last Reminder"
                                                                        description="Send final reminder before appointment"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <SettingInput
                                                                        name="minutesToAppt"
                                                                        label="Minutes Before Appointment"
                                                                        type="number"
                                                                        description="Send reminder minutes before appointment time"
                                                                        register={register}
                                                                        validation={{ min: 0, max: 1440 }}
                                                                        error={errors.minutesToAppt}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Special Notifications */}
                                                <div className="col-xl-12">
                                                    <div className="card bg-light shadow-sm border-0 h-100">
                                                        <div className="card-header bg-transparent border-0 pb-0">
                                                            <h5 className="card-title mb-0">
                                                                <i className="bi bi-gift-fill text-primary me-2"></i>
                                                                Special Notifications
                                                            </h5>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row g-3">
                                                                <div className="col-4">
                                                                    <SettingToggle
                                                                        name="isBirthday"
                                                                        label="Birthday Messages"
                                                                        description="Send birthday greetings to patients"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-4">
                                                                    <SettingToggle
                                                                        name="isWelcome"
                                                                        label="Welcome Messages"
                                                                        description="Send welcome messages to new patients"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-4">
                                                                    <SettingToggle
                                                                        name="isThankyou"
                                                                        label="Thank You Messages"
                                                                        description="Send thank you messages after visits"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-4">
                                                                    <SettingToggle
                                                                        name="isClReplacement"
                                                                        label="Contact Lens Replacement"
                                                                        description="Send Contact Lens Replacement messages"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-4">
                                                                    <SettingToggle
                                                                        name="isClReorder"
                                                                        label="Contact Lens Reorder"
                                                                        description="Send Contact Lens Reorder messages"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Summary & Follow-up */}
                                                <div className="col-xl-12">
                                                    <div className="card bg-light shadow-sm border-0 h-100">
                                                        <div className="card-header bg-transparent border-0 pb-0">
                                                            <h5 className="card-title mb-0">
                                                                <i className="bi bi-file-text-fill text-primary me-2"></i>
                                                                Summary & Follow-up
                                                            </h5>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row g-3">
                                                                <div className="col-md-6">
                                                                    <SettingToggle
                                                                        name="isApptSummary"
                                                                        label="Appointment Summary"
                                                                        description="Send appointment summary after visit"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <SettingSelect
                                                                        name="apptSummary"
                                                                        label="Appointment Summary"
                                                                        description="Preferred person to send"
                                                                        register={register}
                                                                        options={[
                                                                            { value: 'ECP', label: 'ECP' },
                                                                            { value: 'CLINIC', label: 'CLINIC' },
                                                                            { value: 'BOTH', label: 'BOTH' }
                                                                        ]}
                                                                        error={errors.apptPreference}
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <SettingToggle
                                                                        name="isClGlSummary"
                                                                        label="Contact Lens & Glasses Summary"
                                                                        description="Send contact lens and glasses summary after visit"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <SettingToggle
                                                                        name="isFollowup"
                                                                        label="Follow-up Reminders"
                                                                        description="Send follow-up care reminders"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <SettingToggle
                                                                        name="isClPickup"
                                                                        label="Contact Lens Pickup"
                                                                        description="Notify when contact lenses are ready"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <SettingToggle
                                                                        name="isGlPickup"
                                                                        label="Glasses Pickup"
                                                                        description="Notify when glasses are ready"
                                                                        register={register}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <i className="bi bi-exclamation-triangle display-1 text-muted"></i>
                                <h5 className="text-muted mt-3">No reminder settings found</h5>
                                <p className="text-muted">Reminder settings are not configured for this user</p>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .setting-item {
                    background: white;
                    border-radius: 8px;
                    padding: 1.25rem;
                    border: 1px solid #e9ecef;
                    transition: all 0.2s ease;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                .setting-item:hover {
                    border-color: #6c5ce7;
                    box-shadow: 0 2px 8px rgba(108, 92, 231, 0.1);
                }

                .form-check-input:checked {
                    background-color: #6c5ce7;
                    border-color: #6c5ce7;
                }

                .form-check-input:focus {
                    border-color: #6c5ce7;
                    box-shadow: 0 0 0 0.2rem rgba(108, 92, 231, 0.25);
                }

                .badge {
                    font-size: 0.75rem;
                    padding: 0.5rem 0.75rem;
                    border-radius: 20px;
                }

                .badge.bg-success {
                    background: linear-gradient(135deg, #00b894, #00a085) !important;
                }

                .badge.bg-secondary {
                    background: linear-gradient(135deg, #636e72, #2d3436) !important;
                }

                .display-value {
                    padding: 0.5rem 0;
                    min-height: 38px;
                    display: flex;
                    align-items: center;
                    font-weight: 500;
                }
            `}</style>
        </div>
    );
};

export default UserReminder;